import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router, Event, NavigationEnd } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CarritoModel } from '@models/carritoModel';
import { CarritoService } from '@services/carrito.service';
import { ComunicacionService } from '@services/comunicacion.service';
import { productPath } from '@services/seo-url.util';
import { LoginService } from '@services/login.service';
import { ProductosServicesService } from '@services/productos-services.service';
import { formatMoney } from '../../shared/money-format.util';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { safeLocalStorage, safeSessionStorage } from '@shared/safe-web-storage';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentRoute!: string;
  private user!: SocialUser;
  private loggedIn!: boolean;
  public formGroup!: UntypedFormGroup;
  public nombre!: string;
  public TotalProd!: string;
  public TotalCanasta!: number;
  public carrito: CarritoModel[] = [];
  public TxtBusqueda: string = '';
  public logoPath: string = './Public/Img/LogoClave.png';
  public logoHref: string = '/';

  public sugerencias: any[] = [];
  public mostrarSugerencias: boolean = false;
  public buscando: boolean = false;
  public itemSeleccionado: number = -1;

  private busquedaSubject = new Subject<string>();
  private busquedaSub!: Subscription;

  constructor(
    public router: Router,
    private modal: NgbModal,
    private authService: SocialAuthService,
    private loginService: LoginService,
    private comunicaservices: ComunicacionService,
    private carritoServices: CarritoService,
    private prodService: ProductosServicesService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
        this.cerrarSugerencias();
      }
    });
  }

  ngOnInit() {
    if (!this.isBrowser) return;

    const IdUsuario = safeSessionStorage.getItem('IdUsuario') || '0';
    if (IdUsuario !== '0') {
      this.getVariablesCarrito();
    } else {
      this.getcarritoLocal();
    }
    this.comunicaservices.enviarMensajeObservable.subscribe(response => {
      if (response === 'actualiza') {
        if (IdUsuario !== '0') {
          this.getVariablesCarrito();
        } else {
          this.getcarritoLocal();
        }
      }
    });

    // Búsqueda automática con debounce
    this.busquedaSub = this.busquedaSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe(texto => {
      if (texto.length >= 3) {
        this.cargarSugerencias(texto);
      } else {
        this.sugerencias = [];
        this.mostrarSugerencias = false;
      }
    });
  }

  ngOnDestroy() {
    this.busquedaSub?.unsubscribe();
  }

  private get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  onInput(valor: string) {
    this.itemSeleccionado = -1;
    this.busquedaSubject.next(valor.trim());
  }

  async cargarSugerencias(texto: string) {
    this.buscando = true;
    try {
      const result = await this.prodService.BuscarSugerencias(texto, 7);
      this.sugerencias = result.data ?? [];
      this.mostrarSugerencias = this.sugerencias.length > 0;
    } catch {
      this.sugerencias = [];
      this.mostrarSugerencias = false;
    } finally {
      this.buscando = false;
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (!this.mostrarSugerencias) {
      if (event.key === 'Enter') this.VentanaProductos();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.itemSeleccionado = Math.min(this.itemSeleccionado + 1, this.sugerencias.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.itemSeleccionado = Math.max(this.itemSeleccionado - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.itemSeleccionado >= 0) {
          this.irAProducto(this.sugerencias[this.itemSeleccionado]);
        } else {
          this.VentanaProductos();
        }
        break;
      case 'Escape':
        this.cerrarSugerencias();
        break;
    }
  }

  irAProducto(prod: any) {
    this.cerrarSugerencias();
    const id = prod.IdProducto?.toString() || '0';
    if (this.isBrowser) safeSessionStorage.setItem('IdProd', id);
    this.comunicaservices.enviarMensaje('verProducto:' + id);
    this.router.navigateByUrl(productPath(prod));
  }

  verTodosResultados() {
    this.VentanaProductos();
  }

  cerrarSugerencias() {
    this.mostrarSugerencias = false;
    this.itemSeleccionado = -1;
  }

  onBlur() {
    // Pequeño delay para que el click en sugerencia se registre antes de cerrar
    setTimeout(() => this.cerrarSugerencias(), 200);
  }

  getPrecio(prod: any): string {
    const precio = prod?.Modelos?.[0]?.Precio;
    if (!precio) return '';
    return formatMoney(precio);
  }

  getImagen(prod: any): string {
    return prod?.Modelos?.[0]?.Imagenes?.[0]?.src ?? './Public/Img/Productos/Default.jpg';
  }

  VentanaProductos() {
    const texto = this.TxtBusqueda.trim();
    if (!texto) return;
    this.cerrarSugerencias();
    this.router.navigateByUrl('/prod/0/' + texto);
  }

  OpenRegistro(contenido: any) {
    if (!this.isBrowser) return;

    const IdUsuario = safeSessionStorage.getItem('IdUsuario') || '0';
    if (IdUsuario === '0') {
      this.modal.open(contenido, { size: 'lg' });
    } else {
      this.router.navigateByUrl('/perfil');
    }
  }

  abreCarrito() {
    this.router.navigateByUrl('/cart');
  }

  Continuar() {
    this.router.navigateByUrl('/cart');
  }

  async getVariablesCarrito() {
    if (!this.isBrowser) return;

    let total = 0;
    let totalProd;
    const IdUsuario = safeSessionStorage.getItem('IdUsuario') || '0';
    if (IdUsuario === '0') {
      this.getcarritoLocal();
    } else {
      const DatosCarrito: any = await this.carritoServices.getCarProd(parseInt(IdUsuario));
      if (DatosCarrito.length > 0) {
        for (const child of DatosCarrito) {
          total += parseFloat(child.Total.toString().replace(/[^\d.\-]/g, ''));
        }
        totalProd = DatosCarrito.length.toString();
      }
    }
    this.TotalCanasta = total;
    this.TotalProd = totalProd;
    this.nombre = safeSessionStorage.getItem('Nombre') || '';
  }

  getcarritoLocal() {
    if (!this.isBrowser) return;

    if (safeLocalStorage.getItem('carrito') !== null) {
      let total = 0;
      this.carrito = JSON.parse(safeLocalStorage.getItem('carrito') || '');
      for (const child of this.carrito) {
        total += parseFloat(child.Total.toString().replace(/[^\d.\-]/g, ''));
      }
      this.TotalCanasta = total;
      this.TotalProd = this.carrito.length.toString();
      this.nombre = 'Invitado';
    } else {
      this.TotalCanasta = 0;
      this.TotalProd = '';
      this.nombre = '';
    }
  }
}
